import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../../../config/config';
import Loader from '../../../components/Loader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FiBell, FiPlus, FiX, FiTrash2, FiSearch, FiUsers, FiUserCheck,
    FiCheckCircle, FiRefreshCw, FiChevronLeft, FiChevronRight, FiChevronDown,
    FiUser, FiCalendar, FiInbox, FiSettings, FiTag, FiFolder, FiInfo, FiLink,
    FiAlertTriangle, FiArrowUp, FiArrowDown, FiSend, FiUserPlus, FiExternalLink,
} from 'react-icons/fi';

const TIPO_META = {
    sistema: { label: 'Sistema', icon: FiSettings },
    ticket: { label: 'Ticket', icon: FiTag },
    proyecto: { label: 'Proyecto', icon: FiFolder },
    usuario: { label: 'Usuario', icon: FiUser },
};

const ICONO_OPCIONES = [
    { value: 'campana', label: 'Campana', icon: FiBell },
    { value: 'ticket', label: 'Ticket', icon: FiTag },
    { value: 'info', label: 'Información', icon: FiInfo },
    { value: 'alerta', label: 'Alerta', icon: FiAlertTriangle },
    { value: 'usuario', label: 'Usuario', icon: FiUser },
];

const DESTINATARIO_OPCIONES = [
    { value: 'todos', label: 'Todos', icon: FiUsers },
    { value: 'admins', label: 'Administradores', icon: FiUserCheck },
    { value: 'tecnicos', label: 'Técnicos', icon: FiUserCheck },
    { value: 'admins_tecnicos', label: 'Admins y técnicos', icon: FiUserCheck },
    { value: 'especifico', label: 'Usuario específico', icon: FiUserPlus },
];

const formatearFecha = (fecha) => new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
}).format(new Date(fecha));

const formatearFechaRelativa = (fecha) => {
    const diffMs = Date.now() - new Date(fecha).getTime();
    const minutos = Math.floor(diffMs / 60000);
    if (minutos < 1) return 'ahora';
    if (minutos < 60) return `hace ${minutos}m`;
    const horas = Math.floor(minutos / 60);
    if (horas < 24) return `hace ${horas}h`;
    const dias = Math.floor(horas / 24);
    if (dias < 30) return `hace ${dias}d`;
    return new Date(fecha).toLocaleDateString();
};

const TipoIcono = ({ tipo }) => {
    const Icono = (TIPO_META[tipo] || TIPO_META.sistema).icon;
    return <Icono />;
};

const NotificacionesManager = () => {
    // Formulario de creación
    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [destinatario, setDestinatario] = useState('todos');
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState('');
    const [busquedaUsuario, setBusquedaUsuario] = useState('');
    const [mostrarListaUsuarios, setMostrarListaUsuarios] = useState(false);
    const [tipo, setTipo] = useState('sistema');
    const [titulo, setTitulo] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [link, setLink] = useState('');
    const [icono, setIcono] = useState('campana');
    const [prioridad, setPrioridad] = useState('normal');
    const [enviando, setEnviando] = useState(false);

    // Gestión
    const [cargando, setCargando] = useState(true);
    const [refrescando, setRefrescando] = useState(false);
    const [notificaciones, setNotificaciones] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [estadisticas, setEstadisticas] = useState(null);
    const [seleccionadas, setSeleccionadas] = useState([]);
    const [eliminandoSeleccion, setEliminandoSeleccion] = useState(false);
    const [expandidas, setExpandidas] = useState([]);

    // Filtros
    const [filtroTipo, setFiltroTipo] = useState('todas');
    const [filtroEstado, setFiltroEstado] = useState('todas');
    const [busqueda, setBusqueda] = useState('');
    const [orden, setOrden] = useState('desc');

    // Paginación
    const [paginaActual, setPaginaActual] = useState(1);
    const elementosPorPagina = 15;

    const token = localStorage.getItem('AuthToken');
    const tokenInfo = token ? jwtDecode(token) : {};
    const navigate = useNavigate();

    useEffect(() => {
        if (tokenInfo.role !== 'admin') {
            navigate('/');
            return;
        }
        cargarDatos();
        // eslint-disable-next-line
    }, []);

    const cargarDatos = async (silencioso = false) => {
        if (silencioso) setRefrescando(true);
        try {
            const [respNotif, respUsers, respStats] = await Promise.all([
                axios.get(`${BASE_URL}/notificaciones-admin/todas?limite=200`, { headers: { Authorization: token } }),
                axios.get(`${BASE_URL}/notificaciones-admin/usuarios`, { headers: { Authorization: token } }),
                axios.get(`${BASE_URL}/notificaciones-admin/estadisticas`, { headers: { Authorization: token } }),
            ]);

            if (respNotif.data.success) setNotificaciones(respNotif.data.notificaciones);
            if (respUsers.data.success) setUsuarios(respUsers.data.usuarios);
            if (respStats.data.success) setEstadisticas(respStats.data.estadisticas);
            setSeleccionadas([]);
        } catch (error) {
            console.error('Error al cargar datos:', error);
            toast.error('Error al cargar datos');
        } finally {
            setCargando(false);
            setRefrescando(false);
        }
    };

    const notificacionesFiltradas = useMemo(() => {
        let resultado = [...notificaciones];

        if (filtroTipo !== 'todas') {
            resultado = resultado.filter(n => n.tipo === filtroTipo);
        }

        if (filtroEstado === 'individuales') {
            resultado = resultado.filter(n => n.usuario_destinatario !== null);
        } else if (filtroEstado === 'masivas') {
            resultado = resultado.filter(n => n.usuario_destinatario === null);
        } else if (filtroEstado === 'leidas') {
            resultado = resultado.filter(n => n.usuario_destinatario && n.leida === true);
        } else if (filtroEstado === 'no_leidas') {
            resultado = resultado.filter(n => n.usuario_destinatario && n.leida === false);
        }

        if (busqueda.trim()) {
            const texto = busqueda.toLowerCase();
            resultado = resultado.filter(n =>
                n.titulo?.toLowerCase().includes(texto) ||
                n.mensaje?.toLowerCase().includes(texto) ||
                n.usuario_destinatario?.toLowerCase().includes(texto)
            );
        }

        resultado.sort((a, b) => {
            const diff = new Date(b.createdAt) - new Date(a.createdAt);
            return orden === 'desc' ? diff : -diff;
        });

        return resultado;
    }, [notificaciones, filtroTipo, filtroEstado, busqueda, orden]);

    useEffect(() => {
        setPaginaActual(1);
    }, [filtroTipo, filtroEstado, busqueda, orden]);

    const hayFiltrosActivos = filtroTipo !== 'todas' || filtroEstado !== 'todas' || busqueda.trim() !== '';

    const limpiarFiltros = () => {
        setFiltroTipo('todas');
        setFiltroEstado('todas');
        setBusqueda('');
    };

    const enviarNotificacion = async (e) => {
        e.preventDefault();

        if (!titulo.trim() || !mensaje.trim()) {
            toast.error('El título y mensaje son obligatorios');
            return;
        }

        if (destinatario === 'especifico' && !usuarioSeleccionado) {
            toast.error('Selecciona un usuario destinatario');
            return;
        }

        setEnviando(true);
        try {
            const datos = {
                tipo,
                titulo: titulo.trim(),
                mensaje: mensaje.trim(),
                link: link.trim() || null,
                icono,
                prioridad,
                enviar_a_todos: destinatario === 'todos',
                enviar_a_admins: destinatario === 'admins' || destinatario === 'admins_tecnicos',
                enviar_a_tecnicos: destinatario === 'tecnicos' || destinatario === 'admins_tecnicos',
                usuario_destinatario: destinatario === 'especifico' ? usuarioSeleccionado : null,
            };

            const response = await axios.post(`${BASE_URL}/notificaciones`, datos, {
                headers: { Authorization: token }
            });

            if (response.data.success) {
                toast.success(response.data.message);
                limpiarFormulario();
                setMostrarFormulario(false);
                cargarDatos();
            }
        } catch (error) {
            console.error('Error al enviar notificación:', error);
            toast.error('Error al enviar notificación');
        } finally {
            setEnviando(false);
        }
    };

    const limpiarFormulario = () => {
        setTitulo('');
        setMensaje('');
        setLink('');
        setTipo('sistema');
        setIcono('campana');
        setPrioridad('normal');
        setUsuarioSeleccionado('');
        setBusquedaUsuario('');
        setDestinatario('todos');
    };

    const usuariosFiltrados = usuarios.filter(u => {
        const textoCompleto = `${u.nombre} ${u.apellidos} ${u.usuario} ${u.email}`.toLowerCase();
        return textoCompleto.includes(busquedaUsuario.toLowerCase());
    });

    const seleccionarUsuario = (usuario) => {
        setUsuarioSeleccionado(usuario.usuario);
        setBusquedaUsuario(`${usuario.nombre} ${usuario.apellidos} (@${usuario.usuario})`);
        setMostrarListaUsuarios(false);
    };

    const limpiarSeleccionUsuario = () => {
        setUsuarioSeleccionado('');
        setBusquedaUsuario('');
    };

    const eliminarNotificacion = async (id) => {
        if (!window.confirm('¿Eliminar esta notificación?')) return;

        try {
            const response = await axios.delete(`${BASE_URL}/notificaciones/${id}`, {
                headers: { Authorization: token }
            });

            if (response.data.success) {
                toast.success('Notificación eliminada');
                cargarDatos();
            }
        } catch (error) {
            console.error('Error al eliminar:', error);
            toast.error('Error al eliminar notificación');
        }
    };

    const eliminarSeleccionadas = async () => {
        if (seleccionadas.length === 0) return;
        if (!window.confirm(`¿Eliminar ${seleccionadas.length} notificaciones seleccionadas?`)) return;

        setEliminandoSeleccion(true);
        try {
            await Promise.all(seleccionadas.map(id => axios.delete(`${BASE_URL}/notificaciones/${id}`, {
                headers: { Authorization: token }
            })));
            toast.success('Notificaciones eliminadas');
            cargarDatos();
        } catch (error) {
            console.error('Error al eliminar seleccionadas:', error);
            toast.error('Error al eliminar algunas notificaciones');
        } finally {
            setEliminandoSeleccion(false);
        }
    };

    const limpiarNotificacionesAntiguas = async () => {
        if (!window.confirm('¿Eliminar notificaciones leídas de más de 30 días?')) return;

        try {
            const response = await axios.delete(`${BASE_URL}/notificaciones-admin/limpiar-antiguas?dias=30`, {
                headers: { Authorization: token }
            });

            if (response.data.success) {
                toast.success(`${response.data.eliminadas} notificaciones eliminadas`);
                cargarDatos();
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error al limpiar notificaciones');
        }
    };

    const alternarSeleccion = (id) => {
        setSeleccionadas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const alternarExpandida = (id) => {
        setExpandidas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // Paginación
    const totalPaginas = Math.ceil(notificacionesFiltradas.length / elementosPorPagina);
    const indiceUltimo = paginaActual * elementosPorPagina;
    const indicePrimero = indiceUltimo - elementosPorPagina;
    const notificacionesActuales = notificacionesFiltradas.slice(indicePrimero, indiceUltimo);

    const todasSeleccionadasEnPagina = notificacionesActuales.length > 0 &&
        notificacionesActuales.every(n => seleccionadas.includes(n.id));

    const alternarSeleccionPagina = () => {
        if (todasSeleccionadasEnPagina) {
            setSeleccionadas(prev => prev.filter(id => !notificacionesActuales.some(n => n.id === id)));
        } else {
            setSeleccionadas(prev => [...new Set([...prev, ...notificacionesActuales.map(n => n.id)])]);
        }
    };

    const cambiarPagina = (numeroPagina) => {
        if (numeroPagina >= 1 && numeroPagina <= totalPaginas) {
            setPaginaActual(numeroPagina);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const irAEnlace = (n) => {
        if (!n.link) return;
        if (n.link.startsWith('/')) navigate(n.link);
        else window.open(n.link, '_blank', 'noopener,noreferrer');
    };

    if (cargando) return <div className="loader-fullscreen"><Loader /></div>;

    return (
        <main className="site-width">
            <ToastContainer position="bottom-right" autoClose={3000} />

            <div className="gn-page">
                <button className="boton-volver" onClick={() => navigate('/dashboard')}>
                    ← Volver
                </button>

                <div className="gn-header">
                    <div className="pagina-cabecera">
                        <h2>Gestor de notificaciones</h2>
                        <p>Envía y administra notificaciones para usuarios, técnicos y administradores.</p>
                    </div>
                    <div className="gn-header-acciones">
                        <button
                            className="gn-boton-icono"
                            onClick={() => cargarDatos(true)}
                            disabled={refrescando}
                            title="Actualizar"
                        >
                            <FiRefreshCw className={refrescando ? 'gn-girando' : ''} />
                        </button>
                        <button
                            className="boton-primario gn-boton-nueva"
                            onClick={() => setMostrarFormulario(true)}
                        >
                            <FiPlus /> Nueva notificación
                        </button>
                    </div>
                </div>

                {estadisticas && (
                    <div className="gn-stats">
                        <div className="gn-stat-card">
                            <span className="gn-stat-icono"><FiBell /></span>
                            <div>
                                <p className="gn-stat-valor">{estadisticas.totalNotificaciones}</p>
                                <p className="gn-stat-label">Total notificaciones</p>
                                <p className="gn-stat-sub">{estadisticas.notificacionesMasivas} masivas · {estadisticas.notificacionesIndividuales} individuales</p>
                            </div>
                        </div>
                        <div className="gn-stat-card">
                            <span className="gn-stat-icono"><FiSend /></span>
                            <div>
                                <p className="gn-stat-valor">{estadisticas.individualesNoLeidas}</p>
                                <p className="gn-stat-label">Individuales no leídas</p>
                                <p className="gn-stat-sub">Pendientes de revisión</p>
                            </div>
                        </div>
                        <div className="gn-stat-card">
                            <span className="gn-stat-icono"><FiCheckCircle /></span>
                            <div>
                                <p className="gn-stat-valor">{estadisticas.individualesLeidas}</p>
                                <p className="gn-stat-label">Individuales leídas</p>
                                <p className="gn-stat-sub">{estadisticas.porcentajeLeidasIndividuales}% del total individual</p>
                            </div>
                        </div>
                        <div className="gn-stat-card">
                            <span className="gn-stat-icono"><FiUsers /></span>
                            <div>
                                <p className="gn-stat-valor">{estadisticas.totalUsuariosVerificados}</p>
                                <p className="gn-stat-label">Usuarios verificados</p>
                                <p className="gn-stat-sub">Alcance de notificaciones masivas</p>
                            </div>
                        </div>
                    </div>
                )}

                {mostrarFormulario && (
                    <div className="gn-modal-overlay" onClick={() => setMostrarFormulario(false)}>
                        <div className="gn-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="gn-modal-header">
                                <h3><FiPlus /> Nueva notificación</h3>
                                <button
                                    type="button"
                                    className="gn-boton-cerrar"
                                    onClick={() => setMostrarFormulario(false)}
                                >
                                    <FiX />
                                </button>
                            </div>
                            <form onSubmit={enviarNotificacion} className="gn-modal-form">
                                <div className="campo-formulario">
                                    <label>Destinatario *</label>
                                    <div className="gn-chips">
                                        {DESTINATARIO_OPCIONES.map(({ value, label, icon: Icono }) => (
                                            <button
                                                type="button"
                                                key={value}
                                                className={`gn-chip ${destinatario === value ? 'activo' : ''}`}
                                                onClick={() => {
                                                    setDestinatario(value);
                                                    if (value !== 'especifico') {
                                                        setBusquedaUsuario('');
                                                        setUsuarioSeleccionado('');
                                                    }
                                                }}
                                            >
                                                <Icono /> {label}
                                            </button>
                                        ))}
                                    </div>
                                    {destinatario === 'especifico' && (
                                        <div className="selector-usuario-busqueda">
                                            <div className="campo-busqueda-usuario">
                                                <FiSearch className="gn-icono-input" />
                                                <input
                                                    type="text"
                                                    value={busquedaUsuario}
                                                    onChange={(e) => {
                                                        setBusquedaUsuario(e.target.value);
                                                        setMostrarListaUsuarios(true);
                                                        if (!e.target.value) setUsuarioSeleccionado('');
                                                    }}
                                                    onFocus={() => setMostrarListaUsuarios(true)}
                                                    placeholder="Buscar usuario por nombre, apellidos o usuario..."
                                                    className="input-busqueda-usuario gn-input-con-icono"
                                                />
                                                {usuarioSeleccionado && (
                                                    <button
                                                        type="button"
                                                        className="boton-limpiar-usuario"
                                                        onClick={limpiarSeleccionUsuario}
                                                        title="Limpiar selección"
                                                    >
                                                        <FiX />
                                                    </button>
                                                )}
                                            </div>
                                            {mostrarListaUsuarios && busquedaUsuario && (
                                                <div className="lista-usuarios-desplegable">
                                                    {usuariosFiltrados.length > 0 ? (
                                                        usuariosFiltrados.map(u => (
                                                            <div
                                                                key={u.usuario}
                                                                className="item-usuario-desplegable"
                                                                onClick={() => seleccionarUsuario(u)}
                                                            >
                                                                <div className="info-usuario-item">
                                                                    <span className="nombre-usuario-item">
                                                                        {u.nombre} {u.apellidos}
                                                                    </span>
                                                                    <span className="usuario-item">@{u.usuario}</span>
                                                                </div>
                                                                <span className="email-usuario-item">{u.email}</span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="sin-resultados-usuario">
                                                            No se encontraron usuarios
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="grid-formulario">
                                    <div className="campo-formulario">
                                        <label>Tipo de notificación</label>
                                        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="selector">
                                            {Object.entries(TIPO_META).map(([valor, meta]) => (
                                                <option key={valor} value={valor}>{meta.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="campo-formulario">
                                        <label>Prioridad</label>
                                        <select value={prioridad} onChange={(e) => setPrioridad(e.target.value)} className="selector">
                                            <option value="baja">Baja</option>
                                            <option value="normal">Normal</option>
                                            <option value="alta">Alta</option>
                                            <option value="urgente">Urgente</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="campo-formulario">
                                    <label>Icono</label>
                                    <div className="gn-chips">
                                        {ICONO_OPCIONES.map(({ value, label, icon: Icono }) => (
                                            <button
                                                type="button"
                                                key={value}
                                                className={`gn-chip ${icono === value ? 'activo' : ''}`}
                                                onClick={() => setIcono(value)}
                                            >
                                                <Icono /> {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="campo-formulario">
                                    <label>Título *</label>
                                    <input
                                        type="text"
                                        value={titulo}
                                        onChange={(e) => setTitulo(e.target.value)}
                                        placeholder="Título de la notificación"
                                        maxLength={255}
                                        required
                                    />
                                </div>

                                <div className="campo-formulario">
                                    <label>Mensaje *</label>
                                    <textarea
                                        value={mensaje}
                                        onChange={(e) => setMensaje(e.target.value)}
                                        placeholder="Contenido del mensaje"
                                        rows={4}
                                        required
                                    />
                                </div>

                                <div className="campo-formulario">
                                    <label>Enlace (opcional)</label>
                                    <div className="gn-campo-icono">
                                        <FiLink className="gn-icono-input" />
                                        <input
                                            type="text"
                                            value={link}
                                            onChange={(e) => setLink(e.target.value)}
                                            placeholder="/ruta-interna o https://ejemplo.com"
                                            className="gn-input-con-icono"
                                        />
                                    </div>
                                    <small>Ruta interna (ej: /ticket/123) o URL completa</small>
                                </div>

                                <div className="botones-formulario">
                                    <button type="submit" className="boton-primario" disabled={enviando}>
                                        <FiSend /> {enviando ? 'Enviando…' : 'Enviar notificación'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                <div className="gn-toolbar">
                    {seleccionadas.length > 0 ? (
                        <div className="gn-toolbar-seleccion">
                            <span className="gn-toolbar-seleccion-info">
                                <FiCheckCircle /> {seleccionadas.length} seleccionadas
                            </span>
                            <button className="gn-boton-cancelar" onClick={() => setSeleccionadas([])}>
                                Cancelar
                            </button>
                            <button
                                className="gn-boton-peligro"
                                onClick={eliminarSeleccionadas}
                                disabled={eliminandoSeleccion}
                            >
                                <FiTrash2 /> {eliminandoSeleccion ? 'Eliminando…' : 'Eliminar seleccionadas'}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="gn-campo-icono gn-buscador">
                                <FiSearch className="gn-icono-input" />
                                <input
                                    type="text"
                                    placeholder="Buscar por título, mensaje o usuario..."
                                    value={busqueda}
                                    onChange={(e) => setBusqueda(e.target.value)}
                                    className="campo-busqueda gn-input-con-icono"
                                />
                            </div>
                            <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} className="selector">
                                <option value="todas">Todos los tipos</option>
                                {Object.entries(TIPO_META).map(([valor, meta]) => (
                                    <option key={valor} value={valor}>{meta.label}</option>
                                ))}
                            </select>
                            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className="selector">
                                <option value="todas">Todos los estados</option>
                                <option value="individuales">Individuales</option>
                                <option value="masivas">Masivas</option>
                                <option value="no_leidas">Individuales no leídas</option>
                                <option value="leidas">Individuales leídas</option>
                            </select>
                            <button
                                className="gn-boton-icono"
                                onClick={() => setOrden(o => o === 'desc' ? 'asc' : 'desc')}
                                title={orden === 'desc' ? 'Más recientes primero' : 'Más antiguas primero'}
                            >
                                {orden === 'desc' ? <FiArrowDown /> : <FiArrowUp />}
                            </button>
                            <button className="gn-boton-peligro-outline" onClick={limpiarNotificacionesAntiguas}>
                                <FiTrash2 /> Limpiar antiguas
                            </button>
                        </>
                    )}
                </div>

                <div className="gn-lista">
                    <div className="gn-lista-cabecera">
                        {notificacionesActuales.length > 0 && (
                            <label className="gn-checkbox-todos">
                                <input
                                    type="checkbox"
                                    checked={todasSeleccionadasEnPagina}
                                    onChange={alternarSeleccionPagina}
                                />
                                Seleccionar página
                            </label>
                        )}
                        <p className="info-resultados">
                            Mostrando {notificacionesActuales.length} de {notificacionesFiltradas.length} notificaciones
                        </p>
                    </div>

                    {notificacionesActuales.length > 0 ? (
                    <div className="gn-panel">
                        {notificacionesActuales.map((notif) => {
                            const abierta = expandidas.includes(notif.id);
                            return (
                            <div
                                key={notif.id}
                                className={`gn-row ${notif.leida ? 'leida' : 'no-leida'} ${seleccionadas.includes(notif.id) ? 'seleccionada' : ''} ${abierta ? 'expandida' : ''}`}
                            >
                                <div className="gn-row-compacta" onClick={() => alternarExpandida(notif.id)}>
                                    <input
                                        type="checkbox"
                                        className="gn-card-checkbox"
                                        checked={seleccionadas.includes(notif.id)}
                                        onChange={() => alternarSeleccion(notif.id)}
                                        onClick={(e) => e.stopPropagation()}
                                    />

                                    <span className={`gn-card-icono tipo-${notif.tipo}`}>
                                        <TipoIcono tipo={notif.tipo} />
                                    </span>

                                    <div className="gn-row-titulo">
                                        <h4>{notif.titulo}</h4>
                                        <p className="mensaje-notificacion">{notif.mensaje}</p>
                                    </div>

                                    <div className="gn-row-etiquetas">
                                        <span className={`etiqueta-prioridad prioridad-${notif.prioridad}`}>
                                            {notif.prioridad}
                                        </span>
                                        {notif.estadisticas?.tipo === 'individual' && (
                                            <span className={`etiqueta-estado ${notif.leida ? 'estado-leida' : 'estado-no-leida'}`}>
                                                {notif.leida ? <><FiCheckCircle /> Leída</> : 'No leída'}
                                            </span>
                                        )}
                                        {notif.estadisticas?.tipo === 'masiva' && (
                                            <span className="etiqueta-masiva"><FiUsers /> Masiva</span>
                                        )}
                                    </div>

                                    <span className="gn-row-fecha">{formatearFechaRelativa(notif.createdAt)}</span>

                                    <button
                                        className="boton-eliminar"
                                        onClick={(e) => { e.stopPropagation(); eliminarNotificacion(notif.id); }}
                                        title="Eliminar notificación"
                                    >
                                        <FiTrash2 />
                                    </button>

                                    <span className="gn-row-chevron"><FiChevronDown /></span>
                                </div>

                                {abierta && (
                                    <div className="gn-row-detalle">
                                        <div className="info-principal">
                                            <span className={`etiqueta-tipo tipo-${notif.tipo}`}>
                                                {TIPO_META[notif.tipo]?.label || notif.tipo}
                                            </span>
                                        </div>

                                        {notif.estadisticas && (
                                            <div className="seccion-estadisticas-notif">
                                                {notif.estadisticas.tipo === 'masiva' ? (
                                                    <>
                                                        <div className="stat-item">
                                                            <span className="stat-icono"><FiUsers /></span>
                                                            <div className="stat-info">
                                                                <span className="stat-valor">{notif.estadisticas.destinatarios}</span>
                                                                <span className="stat-label">Usuarios alcanzados</span>
                                                            </div>
                                                        </div>
                                                        <div className="stat-item">
                                                            <span className="stat-icono"><FiCheckCircle /></span>
                                                            <div className="stat-info">
                                                                <span className="stat-valor">Verificados</span>
                                                                <span className="stat-label">Estado de usuarios</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="stat-item">
                                                            <span className="stat-icono"><FiUser /></span>
                                                            <div className="stat-info">
                                                                <span className="stat-valor">{notif.estadisticas.destinatario}</span>
                                                                <span className="stat-label">Destinatario único</span>
                                                            </div>
                                                        </div>
                                                        <div className="stat-item">
                                                            <span className="stat-icono">{notif.estadisticas.vista ? <FiCheckCircle /> : <FiInbox />}</span>
                                                            <div className="stat-info">
                                                                <span className="stat-valor">{notif.estadisticas.vista ? 'Leída' : 'No leída'}</span>
                                                                <span className="stat-label">Estado de lectura</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        <div className="info-secundaria">
                                            {notif.link && (
                                                <span className="gn-row-enlace" onClick={() => irAEnlace(notif)}>
                                                    <FiLink /> {notif.link} <FiExternalLink />
                                                </span>
                                            )}
                                            <span><FiUser /> Enviado por <b>{notif.enviado_por}</b></span>
                                            <span><FiCalendar /> {formatearFecha(notif.createdAt)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                    ) : (
                        <div className="gn-vacio">
                            <FiInbox />
                            <p>No se encontraron notificaciones.</p>
                            {hayFiltrosActivos && (
                                <button className="gn-boton-cancelar" onClick={limpiarFiltros}>
                                    Limpiar filtros
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {totalPaginas > 1 && (
                    <div className="paginacion">
                        <button
                            onClick={() => cambiarPagina(paginaActual - 1)}
                            disabled={paginaActual === 1}
                            className="boton-paginacion"
                        >
                            <FiChevronLeft /> Anterior
                        </button>
                        <span className="info-pagina">
                            Página {paginaActual} de {totalPaginas}
                        </span>
                        <button
                            onClick={() => cambiarPagina(paginaActual + 1)}
                            disabled={paginaActual === totalPaginas}
                            className="boton-paginacion"
                        >
                            Siguiente <FiChevronRight />
                        </button>
                    </div>
                )}
            </div>
        </main>
    );
};

export default NotificacionesManager;
